import { NSARouterMakerResult } from 'namirasoft-account-react';
import { CronOperation, NamingConvention } from 'namirasoft-core';
import { FrequencyMonthDaysType, FrequencyMonthNamesType, FrequencyMonthOnDaysType, FrequencyMonthOnWeekType, FrequencyWeekDaysType, SchedulerFrequencyType, SchedulerInputRow, SchedulerRandomDelayUnit, SchedulerRepeatEveryUnit, SchedulerRepeatUntilUnit, SchedulerRow } from 'namirasoft-scheduler';
import { NSBox, NSBoxDateTime, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSBoxTime, NSColumn, NSRow } from 'namirasoft-site-react';
import { Component, createRef } from 'react';
import { SchedulerEntities } from '../SchedulerEntities';
import { SchedulerLayout } from '../SchedulerLayout';
import { SchedulerRouterProps } from '../SchedulerRouterProps';
import UpcomintRuns from '../components/UpcomintRuns';

export interface SchedulerEditPageProps extends NSARouterMakerResult, SchedulerRouterProps
{
    isEdit: boolean;
}

export interface SchedulerEditPageState
{
    frequency_type: SchedulerFrequencyType | null;
}

class SchedulerEditPage extends Component<SchedulerEditPageProps, SchedulerEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxDateTime_StartDateTime = createRef<NSBoxDateTime>();
    NSBoxDateTime_EndDateTime = createRef<NSBoxDateTime>();
    NSBoxTime_StartTime = createRef<NSBoxTime>();
    NSBoxTime_EndTime = createRef<NSBoxTime>();
    NSBoxInteger_MaxRepeatCount = createRef<NSBoxInteger>();
    NSBoxString_CronMinute = createRef<NSBoxString>();
    NSBoxString_CronHour = createRef<NSBoxString>();
    NSBoxEnum_FrequencyType = createRef<NSBoxEnum<SchedulerFrequencyType>>();
    NSBoxInteger_FrequencyDayEvery = createRef<NSBoxInteger>();
    NSBoxInteger_FrequencyWeekEvery = createRef<NSBoxInteger>();
    NSBoxEnum_FrequencyWeekDays = createRef<NSBoxEnum<FrequencyWeekDaysType>>();
    NSBoxEnum_FrequencyMonthNames = createRef<NSBoxEnum<FrequencyMonthNamesType>>();
    NSBoxEnum_FrequencyMonthDays = createRef<NSBoxEnum<FrequencyMonthDaysType>>();
    NSBoxEnum_FrequencyMonthOnWeek = createRef<NSBoxEnum<FrequencyMonthOnWeekType>>();
    NSBoxEnum_FrequencyMonthOnDays = createRef<NSBoxEnum<FrequencyMonthOnDaysType>>();
    NSBoxString_FrequencyCronYear = createRef<NSBoxString>();
    NSBoxString_FrequencyCronMonth = createRef<NSBoxString>();
    NSBoxString_FrequencyCronDay = createRef<NSBoxString>();
    NSBoxString_FrequencyCronDayOfWeek = createRef<NSBoxString>();
    NSBoxInteger_RepeatEvery = createRef<NSBoxInteger>();
    NSBoxInteger_RepeatUntil = createRef<NSBoxInteger>();
    NSBoxInteger_RandomDelay = createRef<NSBoxInteger>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: SchedulerEditPageProps)
    {
        super(props);
        this.state = {
            frequency_type: null
        };
        this.getInputRow = this.getInputRow.bind(this);
    }

    getInputRow(checkError: boolean = true): SchedulerInputRow
    {
        let name = this.NSBoxString_Name.current?.getValue(checkError) ?? "";
        let start_date_time = this.NSBoxDateTime_StartDateTime.current?.getValue(checkError) ?? new Date();
        let end_date_time = this.NSBoxDateTime_EndDateTime.current?.getValue(checkError) ?? null;
        let start_time = this.NSBoxTime_StartTime.current?.getValue(checkError) ?? null;
        let end_time = this.NSBoxTime_EndTime.current?.getValue(checkError) ?? null;
        let max_repeat_count = this.NSBoxInteger_MaxRepeatCount.current?.getValue(checkError) ?? null;
        let cron_minute = this.NSBoxString_CronMinute.current?.getValue() ?? null;
        let cron_hour = this.NSBoxString_CronHour.current?.getValue() ?? null;
        let frequency_type = (this.NSBoxEnum_FrequencyType.current?.getValueOne(checkError) ?? null) as SchedulerFrequencyType;
        let frequency_day_every = this.NSBoxInteger_FrequencyDayEvery.current?.getValue(checkError) ?? null;
        let frequency_week_every = this.NSBoxInteger_FrequencyWeekEvery.current?.getValue(checkError) ?? null;
        let frequency_week_days = this.NSBoxEnum_FrequencyWeekDays.current?.getValueEnumAll(FrequencyWeekDaysType, FrequencyWeekDaysType.Mon) ?? null;
        let frequency_month_names = this.NSBoxEnum_FrequencyMonthNames.current?.getValueEnumAll(FrequencyMonthNamesType, FrequencyMonthNamesType.Jan) ?? null;
        let frequency_month_days = this.NSBoxEnum_FrequencyMonthDays.current?.getValueEnumAll(FrequencyMonthDaysType, FrequencyMonthDaysType.Day_1) ?? null;
        let frequency_month_on_week = this.NSBoxEnum_FrequencyMonthOnWeek.current?.getValueEnumAll(FrequencyMonthOnWeekType, FrequencyMonthOnWeekType.First) ?? null;
        let frequency_month_on_days = this.NSBoxEnum_FrequencyMonthOnDays.current?.getValueEnumAll(FrequencyMonthOnDaysType, FrequencyMonthOnDaysType.Mon) ?? null;
        let frequency_cron_year = this.NSBoxString_FrequencyCronYear.current?.getValue() ?? null;
        let frequency_cron_month = this.NSBoxString_FrequencyCronMonth.current?.getValue() ?? null;
        let frequency_cron_day = this.NSBoxString_FrequencyCronDay.current?.getValue() ?? null;
        let frequency_cron_day_of_week = this.NSBoxString_FrequencyCronDayOfWeek.current?.getValue() ?? null;
        let repeat_every_value = this.NSBoxInteger_RepeatEvery.current?.getValue(checkError) ?? null;
        let repeat_every_unit = (this.NSBoxInteger_RepeatEvery.current?.NSBoxCombo_Ref.current?.getValueOne(checkError) ?? null) as SchedulerRepeatEveryUnit;
        let repeat_until_value = this.NSBoxInteger_RepeatUntil.current?.getValue(checkError) ?? null;
        let repeat_until_unit = (this.NSBoxInteger_RepeatUntil.current?.NSBoxCombo_Ref.current?.getValueOne(checkError) ?? null) as SchedulerRepeatUntilUnit;
        let random_delay_value = this.NSBoxInteger_RandomDelay.current?.getValue(checkError) ?? null;
        let random_delay_unit = (this.NSBoxInteger_RandomDelay.current?.NSBoxCombo_Ref.current?.getValueOne(checkError) ?? null) as SchedulerRandomDelayUnit;
        let description = this.NSBoxTextArea_Description.current?.getValue(checkError) ?? null;

        return {
            workspace_id: "",
            name,
            start_date_time,
            end_date_time,
            start_time,
            end_time,
            max_repeat_count,
            cron_minute,
            cron_hour,
            frequency_type,
            frequency_day_every,
            frequency_week_every,
            frequency_week_days,
            frequency_month_names,
            frequency_month_days,
            frequency_month_on_week,
            frequency_month_on_days,
            frequency_cron_year,
            frequency_cron_month,
            frequency_cron_day,
            frequency_cron_day_of_week,
            repeat_every_value,
            repeat_every_unit,
            repeat_until_value,
            repeat_until_unit,
            random_delay_value,
            random_delay_unit,
            description,
        }
    }

    override render()
    {
        return (
            <SchedulerLayout<SchedulerRow, SchedulerInputRow>
                {...this.props}
                pages={{
                    entity: SchedulerEntities.scheduler(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: () => this.getInputRow(),
                                    setRow: (row: SchedulerRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxDateTime_StartDateTime.current?.setValue(row.start_date_time);
                                        this.NSBoxDateTime_EndDateTime.current?.setValue(row.end_date_time);
                                        this.NSBoxTime_StartTime.current?.setValue(row.start_time);
                                        this.NSBoxTime_EndTime.current?.setValue(row.end_time);
                                        this.NSBoxInteger_MaxRepeatCount.current?.setValue(row.max_repeat_count);
                                        this.NSBoxString_CronMinute.current?.setValue(row.cron_minute);
                                        this.NSBoxString_CronHour.current?.setValue(row.cron_hour);
                                        this.NSBoxEnum_FrequencyType.current?.setValueOne(row.frequency_type);
                                        this.NSBoxInteger_FrequencyDayEvery.current?.setValue(row.frequency_day_every);
                                        this.NSBoxInteger_FrequencyWeekEvery.current?.setValue(row.frequency_week_every);
                                        this.NSBoxEnum_FrequencyWeekDays.current?.setValueAll(row.frequency_week_days ?? []);
                                        this.NSBoxEnum_FrequencyMonthNames.current?.setValueAll(row.frequency_month_names ?? []);
                                        this.NSBoxEnum_FrequencyMonthDays.current?.setValueAll(row.frequency_month_days ?? []);
                                        this.NSBoxEnum_FrequencyMonthOnWeek.current?.setValueAll(row.frequency_month_on_week ?? []);
                                        this.NSBoxEnum_FrequencyMonthOnDays.current?.setValueAll(row.frequency_month_on_days ?? []);
                                        this.NSBoxString_FrequencyCronYear.current?.setValue(row.frequency_cron_year);
                                        this.NSBoxString_FrequencyCronMonth.current?.setValue(row.frequency_cron_month);
                                        this.NSBoxString_FrequencyCronDay.current?.setValue(row.frequency_cron_day);
                                        this.NSBoxString_FrequencyCronDayOfWeek.current?.setValue(row.frequency_cron_day_of_week);
                                        this.NSBoxInteger_RepeatEvery.current?.setValue(row.repeat_every_value);
                                        this.NSBoxInteger_RepeatEvery.current?.NSBoxCombo_Ref.current?.setValueOne(row.repeat_every_unit);
                                        this.NSBoxInteger_RepeatUntil.current?.setValue(row.repeat_until_value);
                                        this.NSBoxInteger_RepeatUntil.current?.NSBoxCombo_Ref.current?.setValueOne(row.repeat_until_unit);
                                        this.NSBoxInteger_RandomDelay.current?.setValue(row.random_delay_value);
                                        this.NSBoxInteger_RandomDelay.current?.NSBoxCombo_Ref.current?.setValueOne(row.random_delay_unit)
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                    },
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Frequency",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<SchedulerFrequencyType>
                                                        ref={this.NSBoxEnum_FrequencyType}
                                                        title="Type"
                                                        required={false}
                                                        nullable
                                                        nullableDefaultValue
                                                        multiple={false}
                                                        getEnumObject={() => SchedulerFrequencyType}
                                                        onChanged={(box) =>
                                                        {
                                                            this.setState(() => ({ frequency_type: box.getValue() as SchedulerFrequencyType }));
                                                        }}
                                                        onNullableChanged={() =>
                                                        {
                                                            if (this.state.frequency_type)
                                                                this.NSBoxEnum_FrequencyType.current?.setValue(null);
                                                        }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {
                                                        this.state.frequency_type === SchedulerFrequencyType.Daily &&
                                                        <NSBoxInteger
                                                            ref={this.NSBoxInteger_FrequencyDayEvery}
                                                            title="Recur every"
                                                            required
                                                            style={{ width: NSBox.width.double }}
                                                            combo={{
                                                                multiple: false,
                                                                getOptions: () => [{ title: "Days", value: "Days" }],
                                                                defaultValue: "Days",
                                                                disabled: true
                                                            }}
                                                        />
                                                    }
                                                    {
                                                        this.state.frequency_type === SchedulerFrequencyType.Weekly &&
                                                        <>
                                                            <NSBoxInteger
                                                                ref={this.NSBoxInteger_FrequencyWeekEvery}
                                                                title="Recur every"
                                                                required
                                                                style={{ width: NSBox.width.double }}
                                                                combo={{
                                                                    multiple: false,
                                                                    getOptions: () => [{ title: "Weeks", value: "Weeks" }],
                                                                    defaultValue: "Weeks",
                                                                    disabled: true
                                                                }}
                                                            />
                                                            <NSBoxEnum<FrequencyWeekDaysType>
                                                                ref={this.NSBoxEnum_FrequencyWeekDays}
                                                                title="On days"
                                                                required={false}
                                                                multiple
                                                                getEnumObject={() => FrequencyWeekDaysType}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                        </>
                                                    }
                                                    {
                                                        this.state.frequency_type === SchedulerFrequencyType.Monthly &&
                                                        <>
                                                            <NSBoxEnum<FrequencyMonthNamesType>
                                                                ref={this.NSBoxEnum_FrequencyMonthNames}
                                                                title="Months"
                                                                required={false}
                                                                nullable
                                                                nullableDefaultValue
                                                                multiple
                                                                getEnumObject={() => FrequencyMonthNamesType}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxEnum<FrequencyMonthDaysType>
                                                                ref={this.NSBoxEnum_FrequencyMonthDays}
                                                                title="Days"
                                                                required={false}
                                                                nullable
                                                                nullableDefaultValue
                                                                multiple
                                                                getEnumObject={() => FrequencyMonthDaysType}
                                                                getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxEnum<FrequencyMonthOnWeekType>
                                                                ref={this.NSBoxEnum_FrequencyMonthOnWeek}
                                                                title="On weeks"
                                                                required={false}
                                                                nullable
                                                                nullableDefaultValue
                                                                multiple
                                                                getEnumObject={() => FrequencyMonthOnWeekType}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxEnum<FrequencyMonthOnDaysType>
                                                                ref={this.NSBoxEnum_FrequencyMonthOnDays}
                                                                title="On week days"
                                                                required={false}
                                                                nullable
                                                                nullableDefaultValue
                                                                multiple
                                                                getEnumObject={() => FrequencyMonthOnDaysType}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                        </>
                                                    }
                                                    {
                                                        this.state.frequency_type === SchedulerFrequencyType.Cron &&
                                                        <>
                                                            <NSBoxString
                                                                ref={this.NSBoxString_FrequencyCronYear}
                                                                title="Year"
                                                                required={false}
                                                                nullable
                                                                nullableDefaultValue
                                                                validator={(name, value) => CronOperation.isValid(value) ? null : `Cron ${name} is not valid.`}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxString
                                                                ref={this.NSBoxString_FrequencyCronMonth}
                                                                title="Month"
                                                                required={false}
                                                                nullable
                                                                nullableDefaultValue
                                                                validator={(name, value) => CronOperation.isValid(value) ? null : `Cron ${name} is not valid.`}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxString
                                                                ref={this.NSBoxString_FrequencyCronDay}
                                                                title="Day"
                                                                required={false}
                                                                nullable
                                                                nullableDefaultValue
                                                                validator={(name, value) => CronOperation.isValid(value) ? null : `Cron ${name} is not valid.`}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <NSBoxString
                                                                ref={this.NSBoxString_FrequencyCronDayOfWeek}
                                                                title="Day of week"
                                                                required={false}
                                                                nullable
                                                                nullableDefaultValue
                                                                validator={(name, value) => CronOperation.isValid(value) ? null : `Cron ${name} is not valid.`}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                        </>
                                                    }
                                                </NSColumn>
                                            )
                                        },
                                        {
                                            title: "Repeat",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxInteger
                                                        ref={this.NSBoxInteger_RepeatEvery}
                                                        title="Repeat task every"
                                                        required={false}
                                                        nullable
                                                        nullableDefaultValue
                                                        combo={{
                                                            multiple: false,
                                                            placeholder: "Unit",
                                                            getOptions: () => Object.values(SchedulerRepeatEveryUnit).map(item => ({ title: item, value: item })),
                                                        }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxInteger
                                                        ref={this.NSBoxInteger_RepeatUntil}
                                                        title="For a duration of"
                                                        required={false}
                                                        nullable
                                                        nullableDefaultValue
                                                        combo={{
                                                            multiple: false,
                                                            placeholder: "Unit",
                                                            getOptions: () => Object.values(SchedulerRepeatUntilUnit).map(item => ({ title: item, value: item })),
                                                        }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSBoxInteger
                                                        ref={this.NSBoxInteger_RandomDelay}
                                                        title="Delay task for up to (random delay)"
                                                        required={false}
                                                        nullable
                                                        nullableDefaultValue
                                                        combo={{
                                                            multiple: false,
                                                            placeholder: "Unit",
                                                            getOptions: () => Object.values(SchedulerRandomDelayUnit).map(item => ({ title: item, value: item })),
                                                        }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                </NSColumn>
                                            )
                                        },
                                        {
                                            title: "Upcoming Runs",
                                            clicked: true,
                                            getContent: () => (
                                                <UpcomintRuns
                                                    {...this.props}
                                                    getRow={() => ({
                                                        ...this.getInputRow(false),
                                                        id: "",
                                                        user_id: "",
                                                        workspace_id: "",
                                                        next_schedule: null,
                                                        last_run: null,
                                                        next_run: null,
                                                        state: null,
                                                        total_run: 0,
                                                        finished: false,
                                                        created_at: new Date(),
                                                        updated_at: new Date(),
                                                    })}
                                                />
                                            )
                                        }
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            title="Name"
                            required
                        />
                        <NSBoxDateTime
                            ref={this.NSBoxDateTime_StartDateTime}
                            title="Start date time"
                            required
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxDateTime
                            ref={this.NSBoxDateTime_EndDateTime}
                            title="End date time"
                            required={false}
                            nullable
                            nullableDefaultValue
                        />
                        <NSBoxInteger
                            ref={this.NSBoxInteger_MaxRepeatCount}
                            title="Max repeat count"
                            required={false}
                            nullable
                            nullableDefaultValue
                        />
                    </NSRow>
                    <NSRow style={{ justifyContent: "flex-start" }}>
                        <NSBoxTime
                            ref={this.NSBoxTime_StartTime}
                            title="Start Time"
                            required={false}
                            nullable
                            nullableDefaultValue
                        />
                        <NSBoxTime
                            ref={this.NSBoxTime_EndTime}
                            title="End time"
                            required={false}
                            nullable
                            nullableDefaultValue
                        />
                    </NSRow>
                    <NSBoxString
                        ref={this.NSBoxString_CronMinute}
                        title="Cron Minute"
                        required={false}
                        nullable
                        nullableDefaultValue
                        validator={(name, value) => CronOperation.isValid(value) ? null : `${name} is not valid.`}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxString
                        ref={this.NSBoxString_CronHour}
                        title="Cron Hour"
                        required={false}
                        nullable
                        nullableDefaultValue
                        validator={(name, value) => CronOperation.isValid(value) ? null : `${name} is not valid.`}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </SchedulerLayout>
        )
    }
}
export default SchedulerEditPage;